import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ControlProps, optionIs, rankWith } from "@jsonforms/core";
import { withJsonFormsControlProps } from "@jsonforms/react";
import { Building2, ChevronsUpDown, Handshake, User, X } from "lucide-react";
import React, { useMemo, useState } from "react";

import { useHubSpotObject, useHubSpotSearch } from "@/api/integrations/hubspot";
import { useIntegrationConnection } from "@/hooks/useIntegrationConnection";
import type {
  HubspotCompanyEntity,
  HubspotContactEntity,
  HubspotDealEntity,
} from "@cohesive/api-entities";
import { getEntityId, Spinner } from "@cohesive/ui-components";
import { useDebounce } from "@uidotdev/usehooks";

type HubSpotEntity =
  | HubspotContactEntity
  | HubspotCompanyEntity
  | HubspotDealEntity;

const createHubSpotObjectSearchControl = ({
  objectType,
}: {
  objectType: "contacts" | "companies" | "deals";
}) => {
  return (props: ControlProps) => {
    return <HubSpotObjectSearchControl {...props} objectType={objectType} />;
  };
};

const getLabelSingular = (objectType: "contacts" | "companies" | "deals") => {
  switch (objectType) {
    case "contacts":
      return "Contact";
    case "companies":
      return "Company";
    case "deals":
      return "Deal";
  }
};

const getLabelPlural = (objectType: "contacts" | "companies" | "deals") => {
  switch (objectType) {
    case "contacts":
      return "Contacts";
    case "companies":
      return "Companies";
    case "deals":
      return "Deals";
  }
};

const getEntityIcon = (objectType: "contacts" | "companies" | "deals") => {
  switch (objectType) {
    case "contacts":
      return User;
    case "companies":
      return Building2;
    case "deals":
      return Handshake;
  }
};

interface HubSpotObjectSearchControlProps extends Omit<ControlProps, "data"> {
  objectType: "contacts" | "companies" | "deals";
  data: HubSpotEntity | string | undefined;
}

const HubSpotObjectSearchControl: React.FC<HubSpotObjectSearchControlProps> = ({
  data,
  enabled,
  handleChange,
  path,
  objectType,
  label,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Extract ID from data (handles both string ID and entity object)
  // Handle both string IDs and entity objects
  const selectedId =
    typeof data === "string" ? data : data ? getEntityId(data as any) : null;

  const debounceSearchQuery = useDebounce(searchQuery, 200);

  const integration = useIntegrationConnection("hubspot");

  const { data: searchResults = [], isLoading: loading } = useHubSpotSearch<
    typeof objectType
  >(
    {
      objectType,
      query: debounceSearchQuery,
      integrationConnectionId: integration?.id || "",
      provider: integration?.integration_provider,
    },
    {
      enabled: Boolean(integration),
    },
  );

  const { data: objectData } = useHubSpotObject<typeof objectType>(
    {
      objectType,
      id: selectedId || "",
      integrationConnectionId: integration?.id || "",
      provider: integration?.integration_provider,
    },
    {
      enabled: Boolean(integration && selectedId),
    },
  );

  const selectLabel = useMemo(() => {
    if (!objectData) {
      return `Select ${getLabelSingular(objectType)}...`;
    }

    // objectData is now an entity with data.name
    return objectData.data.name || `Select ${getLabelSingular(objectType)}...`;
  }, [objectData, objectType]);

  // Search results are now entity arrays directly from the toolbox
  const mappedData = searchResults as HubSpotEntity[];

  const Icon = getEntityIcon(objectType);

  return (
    <div>
      {label && <label className="text-sm font-medium">{label}</label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            disabled={!enabled}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            {objectData ? (
              <div className="flex items-center gap-2 overflow-hidden">
                <div className="flex items-center justify-center w-7 h-7 shrink-0 rounded-full bg-muted">
                  <Icon className="w-4! h-4!" />
                </div>
                <span className="truncate">{selectLabel}</span>
              </div>
            ) : (
              <span className="text-muted-foreground">
                Select {getLabelSingular(objectType).toLowerCase()}...
              </span>
            )}
            <div className="flex items-center gap-1 ml-2 shrink-0">
              {objectData && (
                <div
                  role="button"
                  aria-label="Clear selection"
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleChange(path, null);
                  }}
                >
                  <X className="h-4 w-4 opacity-50 hover:opacity-100" />
                </div>
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={`Search ${getLabelPlural(objectType).toLowerCase()}...`}
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              {loading ? (
                <div className="flex items-center justify-center p-4">
                  <Spinner size="sm" />
                </div>
              ) : (
                <>
                  <CommandEmpty>
                    No {getLabelPlural(objectType).toLowerCase()} found.
                  </CommandEmpty>
                  <CommandGroup>
                    {mappedData.map((entity) => {
                      const entityData = entity.data;

                      if (!entityData) {
                        return null;
                      }

                      return (
                        <CommandItem
                          key={entityData.id}
                          value={entityData.id}
                          onSelect={() => {
                            handleChange(path, entity);
                            setOpen(false);
                          }}
                        >
                          <div className="flex items-center justify-center w-7 h-7 shrink-0 rounded-full bg-muted">
                            <Icon className="w-4! h-4!" />
                          </div>
                          <span className="truncate">{entityData.name}</span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export const hubSpotContactTester = rankWith(
  5,
  optionIs("control", "hubspot_contact"),
);

export const HubSpotContactControl = withJsonFormsControlProps(
  createHubSpotObjectSearchControl({ objectType: "contacts" }),
);

export const hubSpotCompanyTester = rankWith(
  5,
  optionIs("control", "hubspot_company"),
);

export const HubSpotCompanyControl = withJsonFormsControlProps(
  createHubSpotObjectSearchControl({ objectType: "companies" }),
);

export const hubSpotDealTester = rankWith(
  5,
  optionIs("control", "hubspot_deal"),
);

export const HubSpotDealControl = withJsonFormsControlProps(
  createHubSpotObjectSearchControl({ objectType: "deals" }),
);
